import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useAppSelector } from '../../redux/hooks';
import { useSiteEntities } from '../../hooks/useSiteEntities';
import SiteSelector from '../SiteSelector';
import {
  Search,
  ChevronUp,
  ChevronDown,
  Database,
  Download
} from 'lucide-react';

const ComponentSpecificationsTab2: React.FC = () => {
  const currentSite = useAppSelector((state) => state.site.currentSite);
  const { entities, isLoadingEntities } = useSiteEntities();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set());
  const [typeSearchTerms, setTypeSearchTerms] = useState<Record<string, string>>({});

  // Filter states
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [modelSearchTerm, setModelSearchTerm] = useState('');
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);

  // Refs for click-away detection
  const modelDropdownRef = useRef<HTMLDivElement>(null);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modelDropdownRef.current && !modelDropdownRef.current.contains(event.target as Node)) {
        setIsModelDropdownOpen(false);
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target as Node)) {
        setIsCategoryDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get unique values for filters
  const allModels = useMemo(() => {
    const models = new Set<string>();
    entities.forEach(entity => {
      const metadata = entity.entity_metadata || entity.metadata || {};
      const modelValue = metadata.Model || metadata.model || metadata.MODEL;
      if (modelValue && typeof modelValue === 'string') {
        models.add(modelValue);
      }
    });
    return Array.from(models).sort();
  }, [entities]);

  const allCategories = useMemo(() => {
    const categories = new Set<string>();
    entities.forEach(entity => {
      const category = entity.concept_category || entity.entity_type_name || 'Unknown';
      categories.add(category);
    });
    return Array.from(categories).sort();
  }, [entities]);

  // Apply page-wide filters first
  const filteredEntities = useMemo(() => {
    return entities.filter(entity => {
      const name = entity.name || entity.entity_name || '';
      const category = entity.concept_category || entity.entity_type_name || '';
      const metadata = entity.entity_metadata || entity.metadata || {};
      const entityModel = metadata.Model || metadata.model || metadata.MODEL || '';

      // Note: Component selection from Network Viewer would need to be handled via Redux
      // For now, we don't filter by selected components

      // Search term filter
      if (searchTerm && !(
        name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entityModel.toLowerCase().includes(searchTerm.toLowerCase())
      )) {
        return false;
      }

      // Model filter
      if (selectedModel && entityModel !== selectedModel) {
        return false;
      }

      // Category filter
      if (selectedCategory && category !== selectedCategory) {
        return false;
      }

      return true;
    });
  }, [entities, searchTerm, selectedModel, selectedCategory]);

  // Group filtered entities by component type
  const entitiesByType = useMemo(() => {
    const grouped: Record<string, typeof entities> = {};

    filteredEntities.forEach(entity => {
      const type = entity.concept_category || entity.entity_type_name || 'Unknown';
      if (!grouped[type]) {
        grouped[type] = [];
      }
      grouped[type].push(entity);
    });

    return grouped;
  }, [filteredEntities]);

  // Sort types by number of nodes (descending), then alphabetically
  const sortedTypes = useMemo(() => {
    return Object.keys(entitiesByType).sort((a, b) => {
      const aCount = entitiesByType[a].length;
      const bCount = entitiesByType[b].length;

      // Sort by count descending (largest first)
      if (aCount !== bCount) {
        return bCount - aCount;
      }

      // If same count, sort alphabetically
      return a.localeCompare(b);
    });
  }, [entitiesByType]);

  const toggleType = (type: string) => {
    const newExpanded = new Set(expandedTypes);
    if (newExpanded.has(type)) {
      newExpanded.delete(type);
    } else {
      newExpanded.add(type);
    }
    setExpandedTypes(newExpanded);
  };

  const updateTypeSearch = (type: string, value: string) => {
    setTypeSearchTerms(prev => ({ ...prev, [type]: value }));
  };

  const exportToJSON = () => {
    const exportData = entities.map(entity => ({
      name: entity.name || entity.entity_name,
      id: entity.id || entity.entity_id,
      type: entity.concept_category || entity.entity_type_name,
      asset_tag: entity.asset_tag,
      status: entity.status,
      installation_date: entity.installation_date,
      metadata: entity.entity_metadata || entity.metadata || {}
    }));

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `components-export-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Keys to hide from the table
  const hiddenKeys = new Set(['aggregate_specs', 'aggregate specs']);

  // Get all unique property/configuration keys for a given type
  const getKeysForType = (typeEntities: typeof entities): { configKeys: string[]; metadataKeys: string[] } => {
    const configKeys = new Set<string>();
    const metadataKeys = new Set<string>();

    typeEntities.forEach(entity => {
      const configuration = (entity as any).configuration || {};
      const metadata = entity.entity_metadata || entity.metadata || entity.properties || {};

      Object.keys(configuration).forEach(key => {
        if (!hiddenKeys.has(key.toLowerCase())) configKeys.add(key);
      });
      Object.keys(metadata).forEach(key => {
        if (!hiddenKeys.has(key.toLowerCase())) metadataKeys.add(key);
      });
    });

    return {
      configKeys: Array.from(configKeys).sort(),
      metadataKeys: Array.from(metadataKeys).sort()
    };
  };

  // Get the most common value for a key in a type (for reference)
  const getMostCommonValue = (typeEntities: typeof entities, key: string): string => {
    const values: string[] = [];

    typeEntities.forEach(entity => {
      const metadata = entity.entity_metadata || entity.metadata || entity.properties || {};
      const value = metadata[key];
      if (value !== null && value !== undefined) {
        values.push(String(value));
      }
    });

    if (values.length === 0) return '';

    // Count occurrences
    const counts: Record<string, number> = {};
    values.forEach(val => {
      counts[val] = (counts[val] || 0) + 1;
    });

    // Find most common
    let maxCount = 0;
    let mostCommon = '';
    Object.entries(counts).forEach(([val, count]) => {
      if (count > maxCount) {
        maxCount = count;
        mostCommon = val;
      }
    });

    return mostCommon;
  };

  // Check if a value is missing/empty
  const isMissing = (value: any): boolean => {
    return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
  };

  // Check if other entities in the type have this property
  const hasValuesInType = (typeEntities: typeof entities, key: string): boolean => {
    return typeEntities.some(entity => {
      const metadata = entity.entity_metadata || entity.metadata || entity.properties || {};
      return !isMissing(metadata[key]);
    });
  };

  const formatCellValue = (value: any): string => {
    if (isMissing(value)) return '';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value).replace(/_/g, ' ');
  };

  // Utility function to format text: replace underscores with spaces and capitalize
  const formatText = (text: string): string => {
    return text
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const formatColumnHeader = (key: string): string => {
    return formatText(key);
  };

  // Show loading state
  if (isLoadingEntities) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-engramiq-dark-blue text-engramiq-light-grey">
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-engramiq-green border-t-transparent rounded-full animate-spin mb-4"></div>
          <div>Loading components...</div>
        </div>
      </div>
    );
  }

  // Show "No Site Selected" only if no site is selected in Redux
  if (!currentSite) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center bg-engramiq-dark-blue text-engramiq-light-grey p-6">
        <div className="flex flex-col items-center mb-6">
          <Database size={48} className="mb-4 opacity-50" />
          <div className="text-xl font-semibold mb-2">No Site Selected</div>
          <p className="mt-2 text-sm opacity-75 mb-4">Please select a site to view components.</p>
        </div>
        <div className="w-full max-w-md">
          <SiteSelector />
        </div>
      </div>
    );
  }

  // Show empty state if site is selected but no entities loaded yet (and not loading)
  if (entities.length === 0 && !isLoadingEntities) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center bg-engramiq-dark-blue text-engramiq-light-grey p-6">
        <div className="flex flex-col items-center mb-6">
          <Database size={48} className="mb-4 opacity-50" />
          <div className="text-xl font-semibold mb-2">No Components Available</div>
          <p className="mt-2 text-sm opacity-75 mb-4">This site has no components to display.</p>
        </div>
        <div className="w-full max-w-md">
          <SiteSelector />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col bg-engramiq-dark-blue">
      {/* Header */}
      <div className="flex-shrink-0 p-6 border-b border-engramiq-green/20">
        {/* Site Selector */}
        <div className="mb-4">
          <SiteSelector />
        </div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-engramiq-light-grey">Component List</h2>
          {/* <button
            onClick={exportToJSON}
            className="px-4 py-2 bg-transparent hover:bg-engramiq-green/10 border border-engramiq-green/30 hover:border-engramiq-green rounded-lg text-sm text-engramiq-green hover:text-engramiq-green transition-all flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Bulk Export
          </button> */}
        </div>
        <div className="text-sm text-engramiq-bright-grey mb-4">
          {filteredEntities.length} component{filteredEntities.length !== 1 ? 's' : ''} available
        </div>

        {/* Page-wide Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-engramiq-bright-grey" />
          <input
            type="text"
            placeholder="Search components..."
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-surface-800 border border-engramiq-green/20 rounded-lg text-engramiq-light-grey placeholder-engramiq-bright-grey focus:outline-none focus:border-engramiq-green focus:ring-1 focus:ring-engramiq-green transition-all"
          />
        </div>

        {/* Filters */}
        <div className="grid grid-cols-2 gap-3">
          {/* Model Filter */}
          <div className="relative" ref={modelDropdownRef}>
            <label className="block text-xs text-engramiq-bright-grey mb-1">Model</label>
            <div className="relative">
              <button
                onClick={() => setIsModelDropdownOpen(!isModelDropdownOpen)}
                className="w-full px-3 py-2 bg-surface-800 border border-engramiq-green/20 rounded-lg text-engramiq-light-grey text-sm text-left focus:outline-none focus:border-engramiq-green hover:border-engramiq-green/40 transition-all flex items-center justify-between"
              >
                <span className="truncate">{selectedModel ? formatText(selectedModel) : 'All Models'}</span>
                {isModelDropdownOpen ? (
                  <ChevronUp className="w-4 h-4 text-engramiq-bright-grey flex-shrink-0 ml-2" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-engramiq-bright-grey flex-shrink-0 ml-2" />
                )}
              </button>
              {isModelDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-surface-800 border border-engramiq-green/30 rounded-lg shadow-lg z-50 max-h-60 overflow-hidden flex flex-col">
                  <div className="p-2 border-b border-engramiq-green/20">
                    <input
                      type="text"
                      placeholder="Filter models..."
                      value={modelSearchTerm}
                      onChange={(e) => setModelSearchTerm(e.target.value)}
                      className="w-full px-2 py-1 bg-surface-900 border border-engramiq-green/20 rounded text-engramiq-light-grey text-xs focus:outline-none focus:border-engramiq-green"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="overflow-y-auto">
                    <button
                      onClick={() => {
                        setSelectedModel('');
                        setIsModelDropdownOpen(false);
                        setModelSearchTerm('');
                      }}
                      className="w-full px-3 py-2 text-left text-xs hover:bg-engramiq-green/10 transition-colors text-engramiq-light-grey"
                    >
                      All Models
                    </button>
                    {allModels
                      .filter(model =>
                        modelSearchTerm === '' ||
                        model.toLowerCase().includes(modelSearchTerm.toLowerCase())
                      )
                      .map(model => (
                        <button
                          key={model}
                          onClick={() => {
                            setSelectedModel(model);
                            setSelectedCategory(''); // Reset category when model is selected
                            setIsModelDropdownOpen(false);
                            setModelSearchTerm('');
                          }}
                          className={`w-full px-3 py-2 text-left text-xs hover:bg-engramiq-green/10 transition-colors ${
                            selectedModel === model
                              ? 'bg-engramiq-green/20 text-engramiq-light-grey'
                              : 'text-engramiq-light-grey'
                          }`}
                        >
                          {formatText(model)}
                        </button>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Category Filter */}
          <div className="relative" ref={categoryDropdownRef}>
            <label className="block text-xs text-engramiq-bright-grey mb-1">Category</label>
            <div className="relative">
              <button
                onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                className="w-full px-3 py-2 bg-surface-800 border border-engramiq-green/20 rounded-lg text-engramiq-light-grey text-sm text-left focus:outline-none focus:border-engramiq-green hover:border-engramiq-green/40 transition-all flex items-center justify-between"
              >
                <span className="truncate">{selectedCategory ? formatText(selectedCategory) : 'All Categories'}</span>
                {isCategoryDropdownOpen ? (
                  <ChevronUp className="w-4 h-4 text-engramiq-bright-grey flex-shrink-0 ml-2" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-engramiq-bright-grey flex-shrink-0 ml-2" />
                )}
              </button>
              {isCategoryDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-surface-800 border border-engramiq-green/30 rounded-lg shadow-lg z-50 max-h-60 overflow-hidden flex flex-col">
                  <div className="p-2 border-b border-engramiq-green/20">
                    <input
                      type="text"
                      placeholder="Filter categories..."
                      value={categorySearchTerm}
                      onChange={(e) => setCategorySearchTerm(e.target.value)}
                      className="w-full px-2 py-1 bg-surface-900 border border-engramiq-green/20 rounded text-engramiq-light-grey text-xs focus:outline-none focus:border-engramiq-green"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="overflow-y-auto">
                    <button
                      onClick={() => {
                        setSelectedCategory('');
                        setIsCategoryDropdownOpen(false);
                        setCategorySearchTerm('');
                      }}
                      className="w-full px-3 py-2 text-left text-xs hover:bg-engramiq-green/10 transition-colors text-engramiq-light-grey"
                    >
                      All Categories
                    </button>
                    {allCategories
                      .filter(category =>
                        categorySearchTerm === '' ||
                        category.toLowerCase().includes(categorySearchTerm.toLowerCase())
                      )
                      .map(category => (
                        <button
                          key={category}
                          onClick={() => {
                            setSelectedCategory(category);
                            setSelectedModel(''); // Reset model when category is selected
                            setIsCategoryDropdownOpen(false);
                            setCategorySearchTerm('');
                          }}
                          className={`w-full px-3 py-2 text-left text-xs hover:bg-engramiq-green/10 transition-colors ${
                            selectedCategory === category
                              ? 'bg-engramiq-green/20 text-engramiq-light-grey'
                              : 'text-engramiq-light-grey'
                          }`}
                        >
                          {formatText(category)}
                        </button>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {sortedTypes.length === 0 ? (
          <div className="text-center py-8">
            <Database className="w-12 h-12 text-engramiq-bright-grey mx-auto mb-4" />
            <p className="text-engramiq-bright-grey">No components found in this extraction.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {sortedTypes.map(type => {
              const typeEntities = entitiesByType[type];
              const isExpanded = expandedTypes.has(type);
              const typeSearch = typeSearchTerms[type] || '';

              // Filter entities based on search
              const filteredEntities = typeSearch
                ? typeEntities.filter(entity => {
                    const name = entity.name || entity.entity_name || '';
                    const metadata = entity.entity_metadata || entity.metadata || entity.properties || {};
                    const metadataStr = JSON.stringify(metadata).toLowerCase();

                    return (
                      name.toLowerCase().includes(typeSearch.toLowerCase()) ||
                      metadataStr.includes(typeSearch.toLowerCase())
                    );
                  })
                : typeEntities;

              const { configKeys, metadataKeys } = getKeysForType(filteredEntities);
              const allKeys = [...configKeys, ...metadataKeys];

              return (
                <div
                  key={type}
                  className="bg-surface-800/30 border border-engramiq-green/20 rounded-lg overflow-hidden"
                >
                  {/* Type Header - Expandable */}
                  <button
                    onClick={() => toggleType(type)}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-engramiq-green" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-engramiq-green" />
                      )}
                      <span className="text-base font-bold text-engramiq-light-grey uppercase">
                        {formatText(type)}
                      </span>
                      <span className="text-sm text-engramiq-bright-grey">
                        ({typeEntities.length} nodes)
                      </span>
                    </div>
                  </button>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="border-t border-engramiq-green/20 p-4">
                      {/* Search within type */}
                      <div className="mb-4">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-engramiq-bright-grey" />
                          <input
                            type="text"
                            placeholder="Search by component name or metadata..."
                            value={typeSearch}
                            onChange={(e) => updateTypeSearch(type, e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-surface-900 border border-engramiq-green/20 rounded-lg text-engramiq-light-grey placeholder-engramiq-bright-grey focus:outline-none focus:border-engramiq-green focus:ring-1 focus:ring-engramiq-green transition-all text-sm"
                          />
                        </div>
                      </div>

                      {/* Table or No Data Message */}
                      {filteredEntities.length === 0 ? (
                        <div className="text-center py-6">
                          <p className="text-engramiq-bright-grey text-sm">No nodes match the filter.</p>
                        </div>
                      ) : allKeys.length === 0 ? (
                        <div className="space-y-2">
                          <p className="text-engramiq-bright-grey text-sm mb-3">No properties or configuration found for these components.</p>
                          {filteredEntities.slice(0, 50).map(entity => {
                            const name = entity.name || entity.entity_name || 'Unknown';
                            return (
                              <div key={entity.id || entity.entity_id} className="text-sm font-mono text-engramiq-light-grey">
                                {name}
                              </div>
                            );
                          })}
                          {filteredEntities.length > 50 && (
                            <p className="text-engramiq-bright-grey text-sm italic mt-3">
                              Showing first 50 of {filteredEntities.length} nodes.
                            </p>
                          )}
                        </div>
                      ) : (
                        <>
                          {/* Scrollable Table */}
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm border-collapse">
                              <thead>
                                <tr className="bg-engramiq-dark-blue/50 border-b-2 border-engramiq-green/30">
                                  <th className="text-left px-3 py-2 text-engramiq-green font-semibold border-r border-engramiq-green/20 sticky left-0 bg-engramiq-dark-blue z-10">
                                    Component Name
                                  </th>
                                  <th className="text-left px-3 py-2 text-engramiq-green font-semibold border-r border-engramiq-green/20 min-w-[100px]">
                                    Alias
                                  </th>
                                  {configKeys.map(key => (
                                    <th
                                      key={`config-${key}`}
                                      className="text-left px-3 py-2 text-cyan-400 font-semibold border-r border-engramiq-green/20 min-w-[120px]"
                                    >
                                      {formatColumnHeader(key)}
                                    </th>
                                  ))}
                                  {metadataKeys.map(key => (
                                    <th
                                      key={`meta-${key}`}
                                      className="text-left px-3 py-2 text-engramiq-green font-semibold border-r border-engramiq-green/20 min-w-[120px]"
                                    >
                                      {formatColumnHeader(key)}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {filteredEntities.slice(0, 100).map((entity, idx) => {
                                  const name = entity.name || entity.entity_name || 'Unknown';
                                  const entityId = entity.id || entity.entity_id;
                                  const aliases = (entity as any).aliases || [];
                                  const configuration = (entity as any).configuration || {};
                                  const metadata = entity.entity_metadata || entity.metadata || entity.properties || {};

                                  return (
                                    <tr
                                      key={entityId}
                                      className={`border-b border-engramiq-green/10 hover:bg-white/5 transition-colors ${
                                        idx % 2 === 0 ? 'bg-surface-800/20' : 'bg-surface-800/10'
                                      }`}
                                    >
                                      <td className="px-3 py-2 text-engramiq-light-grey font-mono text-xs border-r border-engramiq-green/10 sticky left-0 bg-engramiq-dark-blue">
                                        {name}
                                      </td>
                                      <td className="px-3 py-2 text-engramiq-light-grey text-xs border-r border-engramiq-green/10">
                                        {aliases.length > 0 ? aliases.join(', ') : '—'}
                                      </td>
                                      {configKeys.map(key => {
                                        const value = configuration[key];
                                        const missing = isMissing(value);

                                        return (
                                          <td
                                            key={`config-${key}`}
                                            className="px-3 py-2 border-r border-engramiq-green/10"
                                          >
                                            <span className={`text-xs ${
                                              missing
                                                ? 'text-engramiq-bright-grey/50 italic'
                                                : 'text-engramiq-light-grey'
                                            }`}>
                                              {formatCellValue(value) || '—'}
                                            </span>
                                          </td>
                                        );
                                      })}
                                      {metadataKeys.map(key => {
                                        const value = metadata[key];
                                        const missing = isMissing(value);
                                        const hasOthersWithValue = hasValuesInType(filteredEntities, key);
                                        const shouldHighlight = missing && hasOthersWithValue;

                                        return (
                                          <td
                                            key={`meta-${key}`}
                                            className={`px-3 py-2 border-r border-engramiq-green/10 ${
                                              shouldHighlight
                                                ? 'border-2 border-accent-red/60'
                                                : ''
                                            }`}
                                          >
                                            <span className={`text-xs ${
                                              missing
                                                ? 'text-engramiq-bright-grey/50 italic'
                                                : 'text-engramiq-light-grey'
                                            }`}>
                                              {formatCellValue(value) || '—'}
                                            </span>
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>

                          {filteredEntities.length > 100 && (
                            <div className="mt-3 text-engramiq-bright-grey text-sm italic">
                              Showing first 100 of {filteredEntities.length} nodes. Use search to filter.
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ComponentSpecificationsTab2;
